import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useContent, useSettings } from "@/lib/api";
import { Chip, PageHero, Points, Skeleton, Ticket, Wrap } from "@/night/ui";

const speed = (s) => Math.min(100, (parseInt(String(s).replace(/\D/g, ""), 10) || 0) / 3.2);

function Spotlight({ c }) {
  return (
    <AnimatePresence mode="wait">
      <motion.div key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="grid gap-10 lg:grid-cols-[1.5fr_1fr]" data-testid="car-spotlight">
        <div className="hud relative aspect-[16/10] overflow-hidden border border-[var(--n-line)]">
          <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }} src={c.image} alt={`${c.brand} ${c.name}`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030509]/70 to-transparent" />
          <span className="absolute right-5 top-5"><Chip>{c.category}</Chip></span>
        </div>
        <div className="flex flex-col justify-center">
          <div className="f-lat text-6xl leading-none text-white/90 sm:text-7xl" dir="ltr" style={{ textAlign: "right" }}>{c.brand}</div>
          <h2 className="f-ar mt-2 text-4xl text-[var(--n-cyan)]">{c.name}</h2>
          <p className="mt-5 leading-8 text-slate-300">{c.description}</p>
          <div className="mt-8 space-y-5">
            <div><div className="mb-2 flex justify-between text-xs text-slate-400"><span>أقصى سرعة</span><span className="f-mono">{c.topSpeed}</span></div><div className="speedbar"><i style={{ width: `${speed(c.topSpeed)}%` }} /></div></div>
            <div className="flex items-end justify-between border-t border-[var(--n-line)] pt-5"><span className="text-sm text-slate-400">السعر</span><span className="f-lat text-5xl text-[var(--n-cyan)]" data-testid="car-price">{c.price}</span></div>
          </div>
          <Points items={c.specs} className="mt-8" />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function Cars() {
  const { data = [], isLoading } = useContent("cars");
  const { data: s } = useSettings();
  const [cat, setCat] = useState("الكل");
  const [sel, setSel] = useState(null);
  const cats = ["الكل", ...new Set(data.map((c) => c.category))];
  const list = cat === "الكل" ? data : data.filter((c) => c.category === cat);
  const cur = list.find((c) => c.id === sel) || list[0];
  return (
    <>
      <PageHero en="SHOWROOM" ar="معرض السيارات" desc="اختر سيارتك. السعر، السرعة، وكل التفاصيل قدامك." img="/site/car-bugatti.jpg">
        <div className="mt-10 flex flex-wrap gap-2" data-testid="cars-filters">
          {cats.map((c) => <button key={c} onClick={() => { setCat(c); setSel(null); }} className={`border px-5 py-2.5 text-sm font-bold transition-colors ${cat === c ? "border-[var(--n-cyan)] bg-[var(--n-cyan)] text-[#00131a]" : "border-[var(--n-line)] text-slate-400 hover:text-white"}`} data-testid={`cars-filter-${c}`}>{c}</button>)}
        </div>
      </PageHero>
      {isLoading ? <Skeleton /> : cur && (
        <Wrap className="mt-16">
          <Spotlight c={cur} />
          <div className="mt-14 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="cars-thumbs">
            {list.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)} className={`group relative aspect-[16/10] overflow-hidden border text-right transition-colors ${cur.id === c.id ? "border-[var(--n-cyan)]" : "border-[var(--n-line)] opacity-60 hover:opacity-100"}`} data-testid={`car-${c.id}`}>
                <img src={c.image} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
                <span className="absolute bottom-2 right-3 text-xs font-bold">{c.brand} {c.name}</span>
              </button>
            ))}
          </div>
        </Wrap>
      )}
      <Ticket href={s?.discord_invite} text="لطلب سيارة افتح تذكرة في متجر السيارات بالديسكورد." />
    </>
  );
}
